// src/Userdashboard/pages/Notifications.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBell, FaSpinner, FaCheckCircle, FaTimesCircle,
  FaEye, FaHeart, FaStar, FaUser, FaComment, FaGift,
  FaArrowLeft, FaCheck, FaTrash
} from 'react-icons/fa';
import { getNotifications, markNotificationRead, deleteNotification } from '../../Api/CoreApi';
import { message } from 'antd';

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // ============================================
  // FETCH NOTIFICATIONS
  // ============================================
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await getNotifications(100);
      console.log("📦 All Notifications:", data);
      
      if (data && data.notifications) {
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count || 0);
        setTotalCount(data.total || data.notifications.length);
      }
    } catch (error) {
      console.error("❌ Error fetching notifications:", error);
      message.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // ============================================
  // GET NOTIFICATION ICON
  // ============================================
  const getNotificationIcon = (type) => {
    const icons = {
      'profile_view': '👀',
      'new_profile': '🆕',
      'connection_request': '💖',
      'request_accepted': '✅',
      'request_rejected': '❌',
      'new_plan': '📦',
      'message': '💬',
      'shortlist': '⭐',
      'like': '❤️',
      'comment': '💬',
      'follow': '👤',
      'general': '🔔'
    };
    return icons[type] || '🔔';
  };

  // ============================================
  // MARK AS READ
  // ============================================
  const handleMarkAsRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("❌ Error marking as read:", error);
      message.error("Failed to mark as read");
    }
  };

  // ============================================
  // MARK ALL AS READ
  // ============================================
  const handleMarkAllAsRead = async () => {
    try {
      await markNotificationRead(null);
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
      message.success("All notifications marked as read");
    } catch (error) {
      console.error("❌ Error marking all as read:", error);
      message.error("Failed to mark all as read");
    }
  };

  // ============================================
  // DELETE NOTIFICATION
  // ============================================
  const handleDelete = async (id) => {
    try {
      await deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      message.success("Notification deleted");
    } catch (error) {
      console.error("❌ Error deleting notification:", error);
      message.error("Failed to delete");
    }
  };

  // ============================================
  // GET LINK
  // ============================================
  const getNotificationLink = (notification) => {
    if (notification.link) return notification.link;
    
    switch (notification.notification_type) {
      case 'profile_view':
      case 'new_profile':
        return `/user-profile/${notification.from_user_id}`;
      case 'connection_request':
        return '/requests';
      case 'request_accepted':
        return `/messages?chat=${notification.from_user_id}`;
      case 'shortlist':
        return `/user-profile/${notification.from_user_id}`;
      case 'new_plan':
        return '/recharge';
      default:
        return '#';
    }
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        flexDirection: 'column',
        gap: 16,
        background: '#fff'
      }}>
        <FaSpinner size={48} style={{ color: '#ff0055', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#888', fontSize: 14, fontFamily: 'Poppins, sans-serif' }}>Loading notifications...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }

        .notif-page { background: #fff; min-height: 100vh; padding: 24px 16px; }
        .notif-wrap { max-width: 900px; margin: 0 auto; }

        .notif-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
        .notif-header-left { display: flex; align-items: center; gap: 12px; }
        .notif-back-btn { padding: 8px 12px; border: 1.5px solid #ffe4ec; border-radius: 12px; background: #fff; cursor: pointer; color: #888; transition: all .3s ease; }
        .notif-back-btn:hover { border-color: #ff0055; color: #ff0055; }
        .notif-title { font-size: 24px; font-weight: 800; color: #1a0a10; margin: 0; display: flex; align-items: center; gap: 10px; }
        .notif-title svg { color: #ff0055; }
        .notif-title-badge { font-size: 13px; background: #ff0055; color: #fff; padding: 2px 12px; border-radius: 999px; font-weight: 700; margin-left: 8px; }
        .notif-header-actions { display: flex; gap: 10px; }

        .notif-mark-all { padding: 9px 18px; border: none; border-radius: 999px; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-weight: 700; font-size: 12px; cursor: pointer; transition: all .3s ease; box-shadow: 0 4px 14px rgba(255,0,85,.2); }
        .notif-mark-all:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(255,0,85,.3); }
        .notif-mark-all:disabled { opacity: 0.5; cursor: not-allowed; }

        .notif-list { display: flex; flex-direction: column; gap: 6px; }
        .notif-item { display: flex; align-items: flex-start; gap: 14px; padding: 16px 20px; background: #fff; border: 1.5px solid #ffe4ec; border-radius: 16px; transition: all .3s ease; cursor: pointer; }
        .notif-item:hover { border-color: #ffb3c8; box-shadow: 0 4px 16px rgba(255,0,85,.06); }
        .notif-item.unread { background: #fff5f7; border-color: #ffd3df; }
        .notif-item.unread::before { content: ''; width: 4px; background: #ff0055; border-radius: 0 4px 4px 0; flex-shrink: 0; }

        .notif-icon { width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
        .notif-content { flex: 1; min-width: 0; }
        .notif-content-title { font-size: 14px; font-weight: 700; color: #1a0a10; margin: 0 0 4px; }
        .notif-content-text { font-size: 13px; color: #666; margin: 0; line-height: 1.5; }
        .notif-content-time { font-size: 11px; color: #bbb; margin: 6px 0 0; font-weight: 500; display: flex; align-items: center; gap: 8px; }

        .notif-actions { display: flex; gap: 6px; flex-shrink: 0; align-items: center; }
        .notif-read-btn { padding: 6px 10px; border: none; border-radius: 8px; background: #4caf50; color: #fff; font-size: 11px; cursor: pointer; font-weight: 600; transition: all .3s ease; }
        .notif-read-btn:hover { background: #388e3c; }
        .notif-read-btn.read { background: #e8f5e9; color: #2e7d32; cursor: default; }
        .notif-delete-btn { padding: 6px 10px; border: none; border-radius: 8px; background: #fce4ec; color: #c62828; font-size: 11px; cursor: pointer; font-weight: 600; transition: all .3s ease; }
        .notif-delete-btn:hover { background: #f8bbd0; }

        .notif-empty { text-align: center; padding: 60px 20px; border: 2px solid #ffe4ec; border-radius: 20px; background: #fff; }
        .notif-empty-icon { font-size: 56px; display: block; margin-bottom: 16px; }
        .notif-empty-title { font-size: 20px; font-weight: 800; color: #c2185b; margin: 0 0 8px; }
        .notif-empty-sub { color: #888; margin: 0 0 20px; font-size: 14px; }

        @media (max-width: 640px) {
          .notif-page { padding: 16px 12px; }
          .notif-header { flex-direction: column; align-items: stretch; }
          .notif-header-actions { justify-content: flex-start; }
          .notif-item { padding: 14px 16px; }
          .notif-content-title { font-size: 13px; }
          .notif-content-text { font-size: 12px; }
        }
      `}</style>

      <div className="notif-page">
        <div className="notif-wrap">

          {/* Header */}
          <div className="notif-header">
            <div className="notif-header-left">
              <button className="notif-back-btn" onClick={() => navigate(-1)}>
                <FaArrowLeft size={14} />
              </button>
              <h1 className="notif-title">
                <FaBell /> Notifications
                {unreadCount > 0 && (
                  <span className="notif-title-badge">{unreadCount} new</span>
                )}
              </h1>
            </div>
            <div className="notif-header-actions">
              {unreadCount > 0 && (
                <button className="notif-mark-all" onClick={handleMarkAllAsRead}>
                  <FaCheck size={12} style={{ marginRight: 6 }} /> Mark All Read
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            marginBottom: '20px',
            padding: '12px 16px',
            background: '#fffafc',
            border: '1.5px solid #ffe4ec',
            borderRadius: '12px',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '13px', color: '#888' }}>
              📬 <strong style={{ color: '#1a0a10' }}>{totalCount}</strong> Total
            </span>
            <span style={{ fontSize: '13px', color: '#888' }}>
              🔴 <strong style={{ color: '#ff0055' }}>{unreadCount}</strong> Unread
            </span>
            <span style={{ fontSize: '13px', color: '#888' }}>
              ✅ <strong style={{ color: '#4caf50' }}>{totalCount - unreadCount}</strong> Read
            </span>
          </div>

          {/* Notifications List */}
          {notifications.length > 0 ? (
            <div className="notif-list">
              {notifications.map((item) => (
                <div 
                  key={item.id} 
                  className={`notif-item ${!item.is_read ? 'unread' : ''}`}
                  onClick={() => {
                    if (!item.is_read) {
                      handleMarkAsRead(item.id);
                    }
                    const link = getNotificationLink(item);
                    if (link && link !== '#') {
                      navigate(link);
                    }
                  }}
                >
                  <div className="notif-icon">
                    {item.icon || getNotificationIcon(item.notification_type)}
                  </div>
                  <div className="notif-content">
                    <p className="notif-content-title">{item.title || 'Notification'}</p>
                    <p className="notif-content-text">{item.message || 'No message'}</p>
                    <p className="notif-content-time">
                      {item.time_ago || 'Just now'}
                      {!item.is_read && (
                        <span style={{ 
                          display: 'inline-block', 
                          width: '8px', 
                          height: '8px', 
                          background: '#ff0055', 
                          borderRadius: '50%',
                          marginLeft: '6px'
                        }} />
                      )}
                    </p>
                  </div>
                  <div className="notif-actions">
                    {!item.is_read && (
                      <button 
                        className="notif-read-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(item.id);
                        }}
                      >
                        Mark Read
                      </button>
                    )}
                    {item.is_read && (
                      <button className="notif-read-btn read" disabled>
                        <FaCheck size={10} /> Read
                      </button>
                    )}
                    <button 
                      className="notif-delete-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                    >
                      <FaTrash size={10} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="notif-empty">
              <span className="notif-empty-icon">🔕</span>
              <h3 className="notif-empty-title">No Notifications</h3>
              <p className="notif-empty-sub">You're all caught up! Check back later for updates.</p>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default Notifications;